// Types para sistema de rolagem de dados

export type DiceType = 'D4' | 'D6' | 'D8' | 'D10' | 'D12' | 'D20' | 'D100';

/** Um grupo da bandeja: "2 dados de 6 lados" (fase 3 do modulo de dados). */
export interface DiceGroup {
  type: DiceType;
  qty: number;
}

/** Regra de resolucao da mesa. Vem do game_config; o jogador nao escolhe. */
export type DiceRuleType = 'somar' | 'maiores' | 'sucessos';

export interface DiceRule {
  tipo: DiceRuleType;
  n: number;
  limiar: number;
  travada: boolean;
}

/** Rolagem salva da campanha: "Ataque com espada = 1d20 + Forca". */
export interface SavedRoll {
  id: string;
  name: string;
  dice: DiceGroup[];
  attribute_ids: string[];
}

/** Teto por jogada — a bandeja precisa caber na tela. */
export const MAX_DADOS_POR_JOGADA = 10;

export interface DiceRollRequest {
  dice_type: DiceType;
  table_id: string;
  character_id?: string;
  session_id?: string;
  /** Teste de atributo (docs/PLANO_TESTE_ATRIBUTO.md): exige character_id.
   *  O servidor le o valor NA FICHA — nada de valor de atributo vem daqui. */
  attribute_id?: string;
  /** So o mestre define dificuldade, ao pedir um teste (fase 3). */
  difficulty?: number;
  bonus?: number;
}

export interface DiceRollResult {
  id: string;
  /** "POOL" quando a jogada foi uma bandeja (ver dice_spec). */
  dice_type: DiceType | 'POOL';
  result: number;
  user_id: string;
  user_name: string;
  table_id: string;
  character_id?: string;
  character_name?: string;
  created_at: string;
  /** Campos do teste de atributo — ausentes na rolagem crua. */
  attribute_id?: string;
  attribute_name?: string;
  attribute_value?: number;
  bonus?: number;
  total?: number;
  difficulty?: number;
  outcome?: 'success' | 'failure';
  /** Vantagem / desvantagem (fase 2 da rolagem classica): rolou DOIS dados e
   *  usou o melhor / o pior. `result` ja e o que valeu; aqui vem o descartado.
   *  Ausentes na rolagem normal. */
  advantage?: 'vantagem' | 'desvantagem';
  dropped_result?: number;
  /** Critico natural (fase 3): 'acerto' no 20, 'erro' no 1 — so no teste com
   *  d20. Derivado no servidor a partir do dado que VALEU; nao ha coluna. */
  critical?: 'acerto' | 'erro';
  /** O que estava sendo TENTADO, nomeado pelo mestre ao pedir o teste
   *  ("arrombar a porta"). Ausente em tudo que nao e pedido do mestre. */
  action_name?: string;
  /** Bandeja (fase 3) — ausentes quando a jogada foi de um dado so.
   *  Numa bandeja, `dice_type` vem como "POOL" e `result` ja e o valor
   *  resolvido pela regra da mesa (soma dos mantidos, ou quantos sucessos). */
  dice_spec?: { type: DiceType; result: number }[];
  rule?: DiceRuleType;
  kept?: number[];
  expression?: string;
  /** Parcelas da ficha somadas (chips de atributo), com valor em snapshot. */
  modifiers?: { id: string; name: string; value: number }[];
  /** Ja virou mensagem no chat? False = registrada, esperando o envio. */
  published?: boolean;
}

export interface DiceHistory {
  rolls: DiceRollResult[];
  total_count: number;
}

export interface DiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  tableId: string;
  characterId?: string;
  onRollComplete?: (result: DiceRollResult) => void;
}

/** Atributo testavel: nome vem do game_config, valor vem da ficha. */
export interface AtributoTestavel {
  id: string;
  name: string;
  value: number;
}

export interface DiceWebSocketMessage {
  type: 'dice_roll';
  data: DiceRollResult;
  timestamp: string;
}

export interface DiceButtonProps {
  diceType: DiceType;
  onClick: (diceType: DiceType) => void;
  disabled?: boolean;
}

export interface DiceResultDisplayProps {
  result: DiceRollResult;
  isOwnRoll?: boolean;
}

// Constantes úteis
export const DICE_TYPES: DiceType[] = ['D4', 'D6', 'D8', 'D10', 'D12', 'D20', 'D100'];

export const DICE_MAX_VALUES: Record<DiceType, number> = {
  D4: 4,
  D6: 6,
  D8: 8,
  D10: 10,
  D12: 12,
  D20: 20,
  D100: 100
};

export const DICE_IMAGES: Record<DiceType, string> = {
  D4: '/assets/dice/d4.png',
  D6: '/assets/dice/d6.png',
  D8: '/assets/dice/d8.png',
  D10: '/assets/dice/d10.png',
  D12: '/assets/dice/d12.png',
  D20: '/assets/dice/d20.png',
  D100: '/assets/dice/d100.png'
};